import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useState, useCallback } from 'react';

import { useAppDispatch } from 'redux-store';
import { groupConfigDevices } from 'redux-store/slices/groupConfigDevices';
import type { GroupConfigDevicesApiResponse } from 'redux-store/slices/groupConfigDevices/types';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';
import groupConfigDevicesThunk from 'redux-store/thunks/groupConfigDevices/groupConfigDevicesThunk';

interface ConfigLoadBalancingData {
  serials: string[];
  log?: string;
  loe?: boolean;
  amp?: number;
  sta?: number;
  dyn?: number;
}
interface UseLoadBalancingConfigProps {
  onSuccess: (result: GroupConfigDevicesApiResponse) => void | Promise<void>;
}

export const useLoadBalancingConfig = ({
  onSuccess,
}: UseLoadBalancingConfigProps): {
  onSave: (data: ConfigLoadBalancingData) => Promise<void>;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
} => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const profile = useProfile();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = useCallback(
    async (data: ConfigLoadBalancingData) => {
      try {
        setLoading(true);
        setError(null);

        const hasLotApiKeys = data.amp !== undefined || data.dyn !== undefined;

        const payload = {
          groupConfigDevices: {
            serials: data.serials,
            configurations: {
              loadBalancing: {
                ...(data.log !== undefined && { log: data.log }),
                ...(data.loe !== undefined && { loe: data.loe }),
                ...(hasLotApiKeys && {
                  lot: {
                    ...(data.amp !== undefined && {
                      amp: data.amp,
                      sta: data.sta,
                    }),
                    ...(data.dyn !== undefined && {
                      dyn: data.dyn,
                    }),
                    ts: Math.floor(Date.now() / 1000) + 1,
                  },
                }),
              },
            },
          },
        };

        const result = await dispatch(
          groupConfigDevicesThunk(payload),
        ).unwrap();

        const filteredResults = result.results.filter(
          device => device.pending !== true,
        );
        dispatch(groupConfigDevices(filteredResults));

        if (profile?.id) {
          await dispatch(getAllDevicesThunk({ userId: profile.id }));
        }

        await onSuccess(result);
      } catch {
        setError(
          t('common:pages.load_balancing.add_load_balancing_group.fail'),
        );
      } finally {
        setLoading(false);
      }
    },
    [dispatch, profile, onSuccess, t],
  );

  return { onSave, loading, error, setError };
};
